import { motion } from 'framer-motion';
import { ArrowUpRight, MapPin, Calendar } from 'lucide-react';

export const ProjectsSection = ({ projects, companyName, loading,companyId }) => {
  if (loading) {
    return (
      <section id="projects" className="py-24 md:py-32 bg-obsidian">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-obsidian-subtle w-48"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-[4/3] bg-obsidian-subtle"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="py-24 md:py-32 bg-obsidian"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-gold" />
            <span className="text-gold text-xs uppercase tracking-[0.2em]">Portfolio</span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white">
            Featured <span className="text-primary">{companyId=="materials" ?"Products":"Projects"}</span>
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl">
            Explore our latest work showcasing innovation and excellence from {companyName}.
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="rounded-2xl project-card group relative aspect-[4/3] bg-obsidian-subtle overflow-hidden"
              data-testid={`project-card-${index}`}
            >
              <img
                src={project.image_url}
                alt={project.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              
              {/* Overlay */}
              <div className="overlay absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-transparent flex flex-col justify-end p-6">
                <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  <h3 className="font-heading text-xl font-semibold text-white mb-2">
                    {project.title}
                  </h3>
                  <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                    {project.description}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <MapPin size={12} />
                      {project.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {project.year}
                    </span>
                  </div>
                </div>
              </div>

              {/* Arrow Icon */}
              <div className="absolute top-4 right-4 w-10 h-10 bg-gold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowUpRight size={20} className="text-obsidian" />
              </div>
            </motion.div>
          ))}
        </div>

        {projects.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No projects available for this company.
          </div>
        )}
      </div>
    </section>
  );
};

export default ProjectsSection;
